import { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Divider,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { casesService, type CaseFile } from "../../services/cases.service";

interface CaseFileEditModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  file: CaseFile | null;
  onSaved: (updated: CaseFile) => void;
}

const LABEL_MAX = 100;
const DESCRIPTION_MAX = 500;

export function CaseFileEditModal({
  opened,
  onClose,
  caseId,
  file,
  onSaved,
}: CaseFileEditModalProps) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opened && file) {
      setLabel(file.label ?? "");
      setDescription(file.description ?? "");
    }
  }, [opened, file]);

  const handleSave = useCallback(async () => {
    if (!file) return;
    setSaving(true);
    try {
      const updated = await casesService.updateFile(caseId, file.id, {
        label: label.trim() || null,
        description: description.trim() || null,
      });
      onSaved(updated);
      notifications.show({
        title: "Saved",
        message: "Document details updated",
        color: "green",
      });
      onClose();
    } catch {
      notifications.show({
        title: "Error",
        message: "Failed to update document",
        color: "red",
      });
    } finally {
      setSaving(false);
    }
  }, [caseId, description, file, label, onClose, onSaved]);

  return (
    <Modal.Root
      opened={opened}
      onClose={saving ? () => undefined : onClose}
      size="md"
      centered
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "85vh",
        },
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Edit Document Details</Modal.Title>
          <Modal.CloseButton disabled={saving} />
        </Modal.Header>

        <Modal.Body style={{ flex: 1, overflow: "auto" }}>
          <Stack gap="md">
            {file && (
              <Text size="xs" c="dimmed" truncate="end">
                {file.name}
              </Text>
            )}
            <TextInput
              label="Label"
              placeholder="e.g., Requisition, Insurance card, Moffitt send-out report"
              value={label}
              maxLength={LABEL_MAX}
              onChange={(e) => setLabel(e.currentTarget.value)}
              disabled={saving}
              data-autofocus
            />
            <Textarea
              label="Description"
              placeholder="Optional notes to help identify this document later"
              value={description}
              maxLength={DESCRIPTION_MAX}
              minRows={3}
              autosize
              onChange={(e) => setDescription(e.currentTarget.value)}
              disabled={saving}
            />
          </Stack>
        </Modal.Body>

        <Divider />
        <Group justify="space-between" p="md">
          <Button variant="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save
          </Button>
        </Group>
      </Modal.Content>
    </Modal.Root>
  );
}

export default CaseFileEditModal;
